// yakuman-collector — a MECHANICAL CPU policy that aims for ONE preset yakuman by keeping the tiles
// that yakuman needs and discarding the rest. It is NOT a "playing AI" (spec §0: 対局AIは作らない):
// there is no opponent modelling, no expected-value, no defense — just a deterministic keep/discard
// rule toward a fixed goal the OWNER selects. The owner (final rule arbiter) explicitly asked for it
// as a richer-than-tsumogiri test harness so yakuman scoring/detection can be playtested live.
//
// Pure & deterministic (no Date/Math.random): the per-seat target in 'random' mode is derived from
// the base seed, and discards are chosen by a total order. Tile-set yakuman are DATA-DRIVEN — any
// catalog yakuman carrying a `tilesSubsetOf` constraint automatically becomes a collectible target.
import type { Intent, StructuralConstraint, TileId, YakuDefMeta } from '@mj/protocol';
import { baseOfAka, compareTiles, isHonor } from '@mj/protocol';

const WINDS: TileId[] = ['z1', 'z2', 'z3', 'z4'];
const DRAGONS: TileId[] = ['z5', 'z6', 'z7'];
const TERMINALS: TileId[] = ['m1', 'm9', 'p1', 'p9', 's1', 's9'];
const YAOCHUU: TileId[] = [...TERMINALS, ...WINDS, ...DRAGONS]; // the 13 kokushi tiles
const GREEN: TileId[] = ['s2', 's3', 's4', 's6', 's8', 'z6']; // ryuuiisou

const suitOf = (t: TileId): string => baseOfAka(t)[0]!; // 'm' | 'p' | 's' | 'z'
const rankOf = (t: TileId): number => Number(baseOfAka(t).slice(1));
const inSet = (set: TileId[]): ((t: TileId) => boolean) => {
  const s = new Set(set);
  return (t) => s.has(baseOfAka(t));
};

interface HandCtx {
  counts: Map<TileId, number>; // base tile -> count
  dominantSuit: 'm' | 'p' | 's'; // the number-suit the hand holds most of (for single-suit targets)
}

export interface YakumanTarget {
  id: string;
  ja: string;
  concealed: boolean; // menzen required → never pon/chii/minkan (ron is still fine)
  /** core tiles to collect hard; everything else is filler. */
  core: (t: TileId, ctx: HandCtx) => boolean;
  /** true: keep non-core tiles by SHAPE (a free group + pair can form, e.g. 大三元/四暗刻).
   *  false: the WHOLE hand must be core tiles (pure tile-set, e.g. 字一色/緑一色). */
  freeShape: boolean;
  special?: 'kokushi' | 'suukantsu';
  /** reachable as a DOUBLE-yakuman+ (国士13面 / 四暗刻単騎 / 純正九蓮 / 大四喜) — used by the
   *  "ダブル役満以上のみ" CPU mode. */
  double2x?: boolean;
}

// Built-in targets for the classic yakuman that aren't expressed as a plain tile-set in the catalog.
const BUILTIN_TARGETS: YakumanTarget[] = [
  { id: 'kokushi', ja: '国士無双', concealed: true, special: 'kokushi', core: inSet(YAOCHUU), freeShape: false, double2x: true },
  { id: 'tsuuiisou', ja: '字一色', concealed: false, core: (t) => isHonor(baseOfAka(t)), freeShape: false },
  { id: 'chinroutou', ja: '清老頭', concealed: false, core: inSet(TERMINALS), freeShape: false },
  { id: 'ryuuiisou', ja: '緑一色', concealed: false, core: inSet(GREEN), freeShape: false },
  { id: 'daisangen', ja: '大三元', concealed: false, core: inSet(DRAGONS), freeShape: true },
  { id: 'daisuushi', ja: '大四喜', concealed: false, core: inSet(WINDS), freeShape: true, double2x: true },
  // single-suit collector (keeps the most-held number suit). It builds a 清一色 shape and reaches the
  // full 九蓮宝燈 pattern only when the draws align — realistically only with 無限牌 on.
  { id: 'chuuren', ja: '九蓮宝燈', concealed: true, core: (t, ctx) => suitOf(t) === ctx.dominantSuit, freeShape: false, double2x: true },
  { id: 'suuankou', ja: '四暗刻', concealed: true, core: () => false, freeShape: true, double2x: true },
  // 四槓子: keep count-heavy tiles toward four quads; the bot ankans any 4-of-a-kind (see TableGame).
  { id: 'suukantsu', ja: '四槓子', concealed: false, core: () => false, freeShape: true, special: 'suukantsu' },
];

// Recursively find a `tilesSubsetOf` tile array inside a (possibly nested) DSL constraint.
function tilesSubsetOf(c: StructuralConstraint | undefined): TileId[] | null {
  if (!c) return null;
  if ('tilesSubsetOf' in c) return c.tilesSubsetOf.tiles;
  for (const key of ['all', 'any', 'none'] as const) {
    if (key in c) {
      for (const sub of (c as Record<string, StructuralConstraint[]>)[key]!) {
        const r = tilesSubsetOf(sub);
        if (r) return r;
      }
    }
  }
  return null;
}

// Data-driven: every catalog yakuman with a tilesSubsetOf constraint is a collectible tile-set target.
function catalogTargets(catalog: YakuDefMeta[]): YakumanTarget[] {
  const out: YakumanTarget[] = [];
  for (const def of catalog) {
    if (def.kind !== 'yakuman' || def.detector.type !== 'dsl') continue;
    const tiles = tilesSubsetOf(def.detector.constraint);
    if (!tiles || tiles.length === 0) continue;
    out.push({ id: def.id, ja: def.name.ja, concealed: false, core: inSet(tiles), freeShape: false });
  }
  return out;
}

export function buildTargets(catalog: YakuDefMeta[]): YakumanTarget[] {
  const builtinIds = new Set(BUILTIN_TARGETS.map((t) => t.id));
  return [...BUILTIN_TARGETS, ...catalogTargets(catalog).filter((t) => !builtinIds.has(t.id))];
}

/** For the Studio control bar: list selectable targets ({id, ja}). */
export function listYakumanTargets(catalog: YakuDefMeta[]): { id: string; ja: string }[] {
  return buildTargets(catalog).map((t) => ({ id: t.id, ja: t.ja }));
}

function makeCtx(hand: TileId[]): HandCtx {
  const counts = new Map<TileId, number>();
  const suitCount: { m: number; p: number; s: number } = { m: 0, p: 0, s: 0 };
  for (const t of hand) {
    const b = baseOfAka(t);
    counts.set(b, (counts.get(b) ?? 0) + 1);
    const s = suitOf(b);
    if (s === 'm' || s === 'p' || s === 's') suitCount[s]++;
  }
  const dominantSuit = (['m', 'p', 's'] as const).reduce((a, b) => (suitCount[b] > suitCount[a] ? b : a), 'm');
  return { counts, dominantSuit };
}

function neighbor(base: TileId, ctx: HandCtx): number {
  if (suitOf(base) === 'z') return 0;
  const r = rankOf(base);
  const s = suitOf(base);
  let n = 0;
  if ((ctx.counts.get(`${s}${r - 1}` as TileId) ?? 0) > 0) n++;
  if ((ctx.counts.get(`${s}${r + 1}` as TileId) ?? 0) > 0) n++;
  return n;
}

// Weight = how much this tile is worth KEEPING toward the target. Lower weight → discard first.
function weight(target: YakumanTarget, t: TileId, ctx: HandCtx): number {
  const base = baseOfAka(t);
  const c = ctx.counts.get(base) ?? 1;
  // 四槓子: pure count — keep tiles you hold the most of (toward quads); dump singletons first. No
  // sequence-neighbour bias (kans, not runs).
  if (target.special === 'suukantsu') return c * 10;
  if (target.core(base, ctx)) return 1000 + c; // collect core tiles hard; prefer keeping more copies
  if (!target.freeShape) return c; // pure tile-set: non-core is filler, dump singletons first
  return c * 4 + neighbor(base, ctx); // keep pairs/triplets + sequence neighbours for the free group+pair
}

// Kokushi is the one hand that wants SINGLES of 13 types (+ one pair), so generic pair-keeping is wrong.
function kokushiDiscard(hand: TileId[], allowed: Set<TileId> | null): TileId {
  const ok = (t: TileId) => !allowed || allowed.has(t);
  const yset = new Set(YAOCHUU);
  const nonYao = hand.filter((t) => !yset.has(baseOfAka(t)) && ok(t));
  if (nonYao.length) return [...nonYao].sort(compareTiles)[0]!;
  // all tiles are yaochuu → drop a redundant copy (we want 13 distinct + 1 pair).
  const ctx = makeCtx(hand);
  let pick: TileId | null = null;
  let pickC = 1;
  for (const t of [...hand].sort(compareTiles)) {
    if (!ok(t)) continue;
    const c = ctx.counts.get(baseOfAka(t)) ?? 1;
    if (c > pickC) { pickC = c; pick = t; }
  }
  return pick ?? [...hand].filter(ok).sort(compareTiles)[0] ?? hand[hand.length - 1]!;
}

/** Choose which tile to discard toward the target. `hand` is the full decision hand (incl. the drawn
 *  tile on a draw turn). `allowed` restricts the choice to engine-legal discards (kuikae/riichi). */
export function pickDiscard(target: YakumanTarget, hand: TileId[], allowed?: Set<TileId>): TileId {
  const allow = allowed && allowed.size > 0 ? allowed : null;
  if (target.special === 'kokushi') return kokushiDiscard(hand, allow);
  const ctx = makeCtx(hand);
  const cands = allow ? hand.filter((t) => allow.has(t)) : hand;
  // When `allow` is given, never fall back to a tile outside it (would be an illegal discard). Only as a
  // last resort (no allowed tile is in hand — shouldn't happen in play) do we widen to the full hand.
  let pool = cands.length > 0 ? cands : allow ? [...allow].filter((t) => hand.includes(t)) : hand;
  if (pool.length === 0) pool = hand;
  let worst: TileId | null = null;
  let worstW = Infinity;
  for (const t of [...pool].sort(compareTiles)) { // sorted → deterministic tie-break (lowest tile)
    const w = weight(target, t, ctx);
    if (w < worstW) { worstW = w; worst = t; }
  }
  return worst ?? pool[pool.length - 1]!;
}

/** Choose a MELD call (pon/minkan) toward the target: take it only to grab a CORE tile and only if the
 *  target tolerates open melds; otherwise pass. Ron is handled by the controller (which gates it on the
 *  win actually being a yakuman — the collector holds out rather than settling for a cheap hand). */
export function pickMeldCall(target: YakumanTarget, moves: Intent[], lastTile: TileId | null): Intent | null {
  if (target.concealed || lastTile === null) return null; // menzen targets never open their hand
  const ctxless: HandCtx = { counts: new Map(), dominantSuit: 'm' }; // open targets never need ctx
  if (!target.core(lastTile, ctxless)) return null; // only call to grab a CORE tile
  return moves.find((m) => m.kind === 'pon') ?? moves.find((m) => m.kind === 'minkan') ?? null;
}

// How many CORE tiles a target's complete hand needs (so distances are comparable across targets).
const CORE_NEEDED: Record<string, number> = { daisangen: 9, daisuushi: 12 }; // 3 / 4 honor triplets

/** Rough distance of a hand from a target (lower = closer): how many more core tiles it must collect.
 *  国士 counts missing yaochuu types; 四暗刻 (no fixed core) counts tiles not yet in a pair/triplet. */
export function targetDistance(target: YakumanTarget, hand: TileId[]): number {
  if (target.special === 'kokushi') {
    const yset = new Set(YAOCHUU);
    const distinct = new Set(hand.map(baseOfAka).filter((t) => yset.has(t)));
    return 13 - distinct.size;
  }
  const ctx = makeCtx(hand);
  if (target.id === 'suuankou') {
    // toitoi-shape: tiles already in a pair/triplet are "good"; the rest must be reworked.
    let paired = 0;
    for (const c of ctx.counts.values()) if (c >= 2) paired += c;
    return Math.max(0, 14 - paired);
  }
  let core = 0;
  for (const t of hand) if (target.core(t, ctx)) core++;
  const need = CORE_NEEDED[target.id] ?? 14; // tile-set / single-suit targets need the whole hand
  return Math.max(0, need - Math.min(core, need));
}

/** Pick the target a hand is CLOSEST to (deterministic: first-min by the given order). */
export function pickClosest(targets: YakumanTarget[], hand: TileId[]): YakumanTarget | null {
  let best: YakumanTarget | null = null;
  let bestD = Infinity;
  for (const t of targets) {
    const d = targetDistance(t, hand);
    if (d < bestD) { bestD = d; best = t; }
  }
  return best;
}
